import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { usePracticeListMemberships } from 'app/hooks/usePracticeListMemberships'
import { useStatusListMemberships } from 'app/hooks/useStatusListMemberships'
import type { ContactJson } from 'app/types/entities'
import { PipelineRail } from './PipelineRail'
import { isSystemPipelineId, mergeSystemPipelines } from './systemPipelines'

type RailHandlers = {
  onMoveStage: (pipelineId: string, stageName: string, pipelineName?: string) => void
  onRemove: (pipelineId: string, pipelineName?: string) => void
  onJoin: (pipelineId: string, stageName: string, pipelineName?: string) => void
}

/**
 * The focus card's rail with the grouped navigation on: practice lists come from the List API, system boards stay
 * on the record. Its own observer because `FocusModeComponent` must not be one, and the memberships hook reads
 * MobX state.
 */
export const PracticeListRail = observer(function PracticeListRail({
  contact,
  implicitPipelineId,
  savingId,
  onMoveStage,
  onRemove,
  onJoin,
}: {
  contact: ContactJson
  implicitPipelineId?: string
  savingId?: string
} & RailHandlers) {
  const practice = usePracticeListMemberships(contact, { enabled: true })
  const status = useStatusListMemberships(contact)
  const catalog = useMemo(
    () => [
      ...mergeSystemPipelines(practice.catalog).filter((entry) => !(entry.id in status.replacing)),
      ...status.catalog,
    ],
    [practice.catalog, status.catalog, status.replacing],
  )
  const railContact = useMemo(() => {
    const systemMemberships = (contact.pipelines ?? []).filter(
      (membership) => isSystemPipelineId(membership?.id) && !(membership!.id in status.replacing),
    )
    return { ...contact, pipelines: [...systemMemberships, ...status.entries, ...practice.entries] } as ContactJson
  }, [contact, practice.entries, status.entries, status.replacing])

  return (
    <PipelineRail
      contact={railContact}
      catalog={catalog}
      implicitPipelineId={implicitPipelineId ? (status.replacing[implicitPipelineId] ?? implicitPipelineId) : undefined}
      savingId={savingId}
      onMoveStage={(id, stage, name) =>
        status.isStatusListId(id)
          ? status.changeStage(id, stage)
          : practice.isPracticeListId(id)
            ? practice.changeStage(id, stage)
            : onMoveStage(id, stage, name)
      }
      onJoin={(id, stage, name) => (practice.isPracticeListId(id) ? practice.add(id, stage) : onJoin(id, stage, name))}
      onRemove={(id, name) => (practice.isPracticeListId(id) ? practice.remove(id) : onRemove(id, name))}
      // A filter list's membership is the rule's, and a status list's is the record's: neither has Remove, nor is offered under Add.
      canRemove={(id) => !status.isStatusListId(id) && (!practice.isPracticeListId(id) || practice.canRemove(id))}
      canJoin={(id) =>
        !status.isStatusListId(id) && (isSystemPipelineId(id) || practice.addable.some((entry) => entry.id === id))
      }
    />
  )
})
